import { omit } from 'lodash-es';
import { DiceSearch } from 'wisestep-api';

import formatLocations from '../../../../utils/formatLocations';
import BasicSearchForm from '../../types/IBasicSearchForm';
import IDiceAdvanceSearch from '../types/IDiceAdvanceSearch';

export default function (values: BasicSearchForm & IDiceAdvanceSearch) {
  const query: DiceSearch = {
    accountId: values.accountId,
    page: values.page,
    pageSize: values.pageSize,
    sortBy: values.sort,
    sortByDirection: values.sortDirection,
  };

  if (values.companies.values.length) {
    query.companyKeyword = values.companies.values.join(` ${values.companies.matchType} `);
  }

  const { salaryRange } = values;
  if (values.excludeSocialOnlyProfiles) {
    if (salaryRange.currency && salaryRange.unitTime && salaryRange.min && salaryRange.max) {
      query.compensation = JSON.stringify({
        ...omit(salaryRange, 'unitTime'),
        frequency: salaryRange.unitTime,
        includeUnspecified: true,
      });
    }
    if (values.employmentType.length) {
      query.employmentType = values.employmentType.join(',');
    }
    if (values.authorizedCountries.length) {
      query.workPermitLocation = values.authorizedCountries.join(',');
    }
    if (values.workAuthorization.length) {
      query.workPermit = values.workAuthorization.join(',');
    }
    if (values.hasSecurityClearance !== null) {
      query.onlyWithSecurityClearance = true;
    }
    if (values.willingToRelocate !== null) {
      query.willingToRelocate = values.willingToRelocate;
    }
    if (values.travelPreference.toString() !== '-1') {
      query.travelPreference = Number(values.travelPreference);
    }
    if (values.resumeLastUpdated) {
      query.dateResumeLastUpdated = parseInt(values.resumeLastUpdated);
    }
    query.searchType = 'Resume';
  } else {
    query.searchType = 'Integrated';
  }

  if (values.education.length) {
    query.educationDegree = values.education.join(',');
  }

  if (values.additionalFilters.includes('excludeThirdParty')) {
    query.excludeThirdParty = true;
  }

  if (values.additionalFilters.includes('hasPatent')) {
    query.hasPatent = true;
  }

  if (values.additionalFilters.includes('excludeFounders')) {
    query.excludeFounders = true;
  }

  if (values.contactMethod.includes('phone')) {
    query.hasPhoneNumber = true;
  }

  if (values.contactMethod.includes('email')) {
    query.hasEmail = true;
  }

  if (values.jobTitle.values.length) {
    const jobTitles = values.jobTitle.values
      .map((title) => JSON.stringify(title))
      .join(values.jobTitle.matchType === 'AND' ? '^' : ',');
    query.jobTitle = jobTitles.toString();
  }

  if (values.languages.length) {
    query.language = values.languages.join(' AND ');
  }

  if (values.lastActive) {
    query.lastActive = values.lastActive;
  }

  if (values.likelyToMove.toString() !== '-1') {
    switch (values.likelyToMove.toString()) {
      case '1':
        query.likelyToMove = JSON.stringify({ min: 1, max: 25 });
        break;
      case '26':
        query.likelyToMove = JSON.stringify({ min: 26, max: 75 });
        break;
      case '76':
        query.likelyToMove = JSON.stringify({ min: 76, max: 100 });
        break;
      default:
        query.likelyToMove = JSON.stringify({ min: 0, max: 0 });
    }
  }

  if (values.locations && values.locations.length) {
    const locations = values.locations.map((location) => ({
      value: formatLocations([{ ...location, point: location.point ?? '' }])[0],
      distance: values.distance,
      distanceUnit: 'miles',
    }));
    query.locations = JSON.stringify(locations);
  }

  if (values.query) {
    query.q = values.query;
  }

  if (values.skills.values.length) {
    query.skillsKeyword = values.skills.values.join(` ${values.skills.matchType} `);
  }

  if (values.willingToRelocatePreferredLocationsOnly !== null) {
    query.willingToRelocatePreferredLocationsOnly = values.willingToRelocatePreferredLocationsOnly;
  }

  if (values.willingToRelocateIncludeLocals !== null) {
    query.willingToRelocateIncludeLocals = values.willingToRelocateIncludeLocals;
  }

  const { totalExperience } = values;

  if (totalExperience.max && !totalExperience.min) {
    query.yearsExperience = JSON.stringify({ max: totalExperience.max });
  } else if (!totalExperience.max && totalExperience.min) {
    query.yearsExperience = JSON.stringify({ min: totalExperience.min });
  } else if (totalExperience.max && totalExperience.min) {
    query.yearsExperience = JSON.stringify({ max: totalExperience.max, min: totalExperience.min });
  }

  return query;
}
